/**
 * Declarative related-entity section configs for the entity slideout.
 *
 * A "related-entity section" shows CRM entities related to the slideout entity
 * — e.g. a contact's upcoming meetings, or a deal's historical meeting notes.
 * Each section is described by *data*: a name, the relationship to the slideout
 * entity, an optional result filter, sort, limit, and which card renders each
 * row. A config resolves to a single
 * `query_objects(associated_with_entity_id=…)` call via `useCrmObjectList`
 * (see `../sections/related-entity-section.tsx` for the renderer).
 *
 * The config is intentionally pure and serializable — cards are referenced by
 * string key, never as embedded components — so the same shape can later be
 * served from the backend for org-customizable slideouts. Today it lives in the
 * TS registry (`entityTypeRegistry.ts`).
 */

import type { ComponentType } from 'react';

import type {
  CrmObject,
  CrmObjectFilters,
  EntityTypeName,
  PropertyFilterOperator,
} from '@/types/crm';

import type { EntityCardKey } from './entityCardRegistry';

/**
 * How the related entities are found from the slideout entity.
 *
 * `association` is the primary (and, today, only) strategy: rows associated
 * with the slideout entity, resolved server-side via `associated_with_entity_id`.
 * It transparently covers CRM-materialized *transitive* associations (e.g.
 * `ahoy_contact_to_meeting_notes`, `ahoy_case_to_meeting_notes`), so a two-hop
 * relationship is read as a single hop.
 *
 * A legacy `property-match` strategy (query a type, match a sub-property against
 * a value derived from the entity — how "Upcoming meetings" used to work) is a
 * planned escape hatch and is deliberately NOT implemented here; new sections
 * use associations.
 */
export type RelatedSectionSource = { kind: 'association' };

/** Comparison operators valid for a section result filter. */
export type RelatedSectionFilterOperator = Extract<
  PropertyFilterOperator,
  'gt' | 'gte' | 'lt' | 'lte' | 'exact' | 'ne'
>;

/** A filter applied to the result set (e.g. future-only meetings). */
export interface RelatedSectionFilter {
  kind: 'property-compare';
  property: string;
  operator: RelatedSectionFilterOperator;
  /**
   * `'now'` resolves to the current time (ISO) when the query is built — see
   * `nowIsoBucketedToMinute` and the renderer. Any other value is used as-is.
   */
  value: 'now' | string | number;
}

/** A declarative related-entity section. */
export interface RelatedEntitySectionConfig {
  /** Stable id, unique within an entity type's section list. */
  key: string;
  /** Section heading shown in the slideout. */
  title: string;
  /** Entity type of the rows shown. */
  entityType: EntityTypeName;
  /** Relationship to the slideout entity. */
  source: RelatedSectionSource;
  /** Optional result filters, ANDed together (all must match). */
  filters?: RelatedSectionFilter[];
  /** Optional sort. */
  sort?: { property: string; order: 'asc' | 'desc' };
  /**
   * Rows shown before truncation (default {@link DEFAULT_SECTION_LIMIT}). When
   * more rows match, the section shows a "View N more …" affordance that
   * expands to fetch up to {@link EXPANDED_SECTION_LIMIT}.
   */
  limit?: number;
  /** Properties fetched for each row (consumed by the card). */
  properties: string[];
  /** Associations to hydrate per row (e.g. company badge on a meeting card). */
  includeAssociations?: Record<string, { properties: string[] }>;
  /** Which card renders each row. */
  card: EntityCardKey;
  /** Empty-state copy shown when there are no rows. */
  emptyState: string;
  /** Section-specific copy shown when the section fails to load. */
  errorMessage: string;
  /** Plural noun for the "View N more <noun>" affordance (e.g. "meetings"). */
  itemNounPlural: string;
  /** Singular noun for the 'View 1 more <noun>' affordance when hiddenCount === 1. Falls back to itemNounPlural. */
  itemNounSingular?: string;
  /**
   * When true, render nothing (no header, no skeleton, no empty-state copy)
   * while loading AND when zero rows match. The error state still renders so a
   * failed load is never silently hidden. `emptyState` stays required but is
   * never shown under this flag.
   */
  hideWhenEmpty?: boolean;
}

/**
 * One entry in an entity type's `typeSpecificSections` list. Either a
 * declarative related-entity section, or a `custom` escape hatch that renders a
 * bespoke component (used while migrating existing hand-built sections).
 */
export type TypeSpecificSectionEntry =
  | { kind: 'section'; config: RelatedEntitySectionConfig }
  | {
      kind: 'custom';
      /** Stable key for React reconciliation. */
      key: string;
      component: ComponentType<{
        object: CrmObject;
        onRequestNoteCreation?: () => void;
      }>;
    };

/** Default rows shown before the "View N more" affordance. */
export const DEFAULT_SECTION_LIMIT = 5;

/** Rows fetched once a section is expanded via "View N more". */
export const EXPANDED_SECTION_LIMIT = 100;

/**
 * Resolve `value: 'now'` to an ISO timestamp bucketed to the current minute.
 * Bucketing keeps the React Query key stable within a minute (so it doesn't
 * churn every render) and lets identical sections share a cache entry. The
 * renderer still memoizes this once per mount.
 */
export function nowIsoBucketedToMinute(): string {
  const d = new Date();
  d.setSeconds(0, 0);
  return d.toISOString();
}

/** Rows shown before truncation for a section config. */
export function sectionDisplayLimit(
  config: RelatedEntitySectionConfig
): number {
  return config.limit ?? DEFAULT_SECTION_LIMIT;
}

/**
 * Build the `useCrmObjectList` filters for a section config. Pure so it can be
 * unit-tested and memoized in the renderer.
 *
 * `expanded` requests the larger {@link EXPANDED_SECTION_LIMIT} (after the user
 * clicks "View N more"); otherwise only the display limit is fetched.
 * `include_total_count` is always set so the renderer knows how many rows match
 * (to compute "View N more") even while showing a truncated page.
 */
export function buildSectionFilters(
  config: RelatedEntitySectionConfig,
  entityId: string,
  nowIso: string,
  expanded = false
): CrmObjectFilters {
  const filters: CrmObjectFilters = {
    associated_with_entity_id: entityId,
    properties: config.properties,
    limit: expanded ? EXPANDED_SECTION_LIMIT : sectionDisplayLimit(config),
    include_total_count: true,
  };

  if (config.sort) {
    filters.sort = config.sort.property;
    filters.order = config.sort.order;
  }

  if (config.filters && config.filters.length > 0) {
    filters.filter_groups = [
      {
        filters: config.filters.map(f => ({
          property: f.property,
          operator: f.operator,
          value: f.value === 'now' ? nowIso : f.value,
        })),
      },
    ];
  }

  return filters;
}
